import { Button, DialogOverlay, DialogOverlayActions, DialogOverlayFooter, Radio, RadioGroup } from '@fs/zion-ui'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserContext } from '../UserContext'
import { downloadUserReservationData } from '../../../services/userService'
import useNotifications from '../../../hooks/useNotifications'
import { downloadFile } from '../../../util/FileDownloadUtil'

export default function UserReservationsDownloadDialog({ disableDownload, showOnlyCompletedOrdinances, ...opener }) {
  const { t } = useTranslation()
  const { currentUser } = useContext(UserContext)
  const [selected, setSelected] = useState('all')
  const { error } = useNotifications()
  const downloadHeaders = [
    t('individualId', 'Individual ID'),
    t('spouse1.id', 'Spouse 1 ID'),
    t('spouse2.id', 'Spouse 2 ID'),
    t('ordinanceType', 'Ordinance Type'),
    t('owner', 'Owner'),
    t('reservation.shared', 'Shared'),
    t('reservedDate', 'Reserved Date'),
    t('reservations.assignedDate', 'Assigned Date'),
    t('reservations.printedDate', 'Printed Date'),
    t('reservations.expirationDate', 'Expiration Date'),
    t('ranId', 'RAN ID'),
  ]

  useEffect(() => {
    if (!opener.isOpen && !opener.transitioning) {
      setSelected('all')
    }
  }, [opener.isOpen, opener.transitioning])

  const handleChange = (e) => {
    setSelected(e.target.value)
  }

  const processReservationItems = (resItem) => {
    if (resItem.ordinanceType === 'SEALING_TO_SPOUSE') {
      resItem.currentIndividualId = resItem.currentSpouse1Id
    }

    const sharedType = t(`reservation.sharedType.${resItem.sharedType}`, resItem.sharedType) || ''
    const groupId = resItem.groupId ? ` - ${resItem.groupId}` : ''

    return [
      resItem.currentIndividualId || '',
      resItem.currentSpouse1Id || '',
      resItem.currentSpouse2Id || '',
      resItem.ordinanceType || '',
      resItem.ownerId || '',
      `${sharedType}${groupId}`,
      resItem.reservedDate || '',
      resItem.assignedDate || '',
      resItem.printedDate || '',
      resItem.expiryDate || '',
      resItem.ranId,
    ].join(',')
  }

  const downloadReservationList = () => {
    if (!disableDownload) {
      opener.close()
      downloadUserReservationData(currentUser.contributorId, showOnlyCompletedOrdinances, selected)
        .then((response) => {
          if (response.data.reservations === null) {
            error(
              t('reservations.errors.noReservations', {
                defaultValue: `No reservations found for selected type`,
                message: `No reservations found for selected type`,
              })
            )
          }

          const stringList = [
            downloadHeaders.join(','),
            ...response.data.reservations.map((resItem) => processReservationItems(resItem)),
          ]
          const reservationString = stringList.join('\n')

          downloadFile(reservationString, `${currentUser.contributorId} reservations.csv`)
        })
        .catch((err) => {
          error(t('reservations.errors.downloadUserReservationData', 'Error downloading user reservations'), err)
        })
    }
  }

  return (
    <DialogOverlay
      {...opener}
      title={t('user.buttons.downloadReservations.description', 'Download Reservations')}
      subtitle={currentUser.dmcUser?.cisId?.split('.')[2]}
    >
      <RadioGroup>
        <Radio
          checked={selected === 'all'}
          label={t('user.buttons.allReservations', 'All Reservations')}
          name={t('user.buttons.allReservations', 'All Reservations')}
          value="all"
          onChange={handleChange}
        />
        <Radio
          checked={selected === 'individual'}
          label={t('user.buttons.individual', 'Individual')}
          name={t('user.buttons.individual', 'Individual')}
          value="individual"
          onChange={handleChange}
        />
        <Radio
          checked={selected === 'group'}
          label={t('user.buttons.sharedWithGroup', 'Shared With Group')}
          name={t('user.buttons.sharedWithGroup', 'Shared With Group')}
          value="group"
          onChange={handleChange}
        />
        <Radio
          checked={selected === 'temple'}
          label={t('user.buttons.sharedWithTemple', 'Shared With Temple')}
          name={t('user.buttons.sharedWithTemple', 'Shared With Temple')}
          value="temple"
          onChange={handleChange}
        />
      </RadioGroup>
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button emphasis="high" onClick={downloadReservationList}>
            {t('action.confirm', 'Confirm')}
          </Button>
          <Button emphasis="medium" onClick={opener.close}>
            {t('action.cancel', 'Cancel')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
